import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { BOARD_NAMES } from "@/components/YourGames";
import BoardChoice from "@/components/BoardChoice";
import UnbrokenChainToggle from "@/components/UnbrokenChainToggle";
import SameTilesToggle from "@/components/SameTilesToggle";
import { toast } from "sonner";
import { Sword, X } from "@phosphor-icons/react";

// Challenge from another player's profile: the same challenge as the lobby's
// username search (pick a board, then it waits in Your games for them).
export default function ChallengeButton({ username }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testUnbroken, setTestUnbroken] = useState(false);
  const [sameTiles, setSameTiles] = useState(false);

  const challenge = async (board) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/challenges", {
        username, layout: board,
        ...(testUnbroken ? { test_rules: "unbroken" } : {}),
        ...(sameTiles ? { same_tiles: true } : {}),
      });
      const toLobby = { label: "Your games", onClick: () => navigate("/lobby") };
      if (r.status === "incoming") {
        toast.info(`${r.challenge.from_username} has already challenged you. Accept it in the lobby.`, { action: toLobby });
      } else if (r.status === "pending") {
        toast.info(`You've already challenged ${r.challenge.to_username}.`, { action: toLobby });
      } else {
        toast.success(`${BOARD_NAMES[r.challenge.layout] || "Game"} challenge sent to ${r.challenge.to_username}.`, { action: toLobby });
      }
      setOpen(false);
      setTestUnbroken(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send the challenge");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider bg-brand text-brand-foreground hover:bg-brand-hover"
        data-testid="profile-challenge"
      >
        <Sword size={15} weight="bold" /> Challenge
      </button>
    );
  }

  return (
    <div className="w-full rounded-xl border border-brand/40 bg-brand/5 p-3" data-testid="profile-challenge-pick-board">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="text-sm min-w-0">
          Challenge <span className="font-semibold">{username}</span> on which board?
        </div>
        <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground flex-none" aria-label="Cancel" data-testid="profile-challenge-cancel">
          <X size={16} />
        </button>
      </div>
      <UnbrokenChainToggle className="mb-2.5" checked={testUnbroken} onChange={setTestUnbroken} testId="profile-challenge-unbroken" />
      <SameTilesToggle className="mb-2.5" checked={sameTiles} onChange={setSameTiles} testId="profile-challenge-same-tiles" />
      <BoardChoice disabled={busy} onPick={(id) => challenge(id)} testIdPrefix="profile-challenge-board" />
    </div>
  );
}
